'use client';
import { useState, useEffect } from 'react';
import cn from 'classnames';

import Text from '@/components/common/Text/Text';
import { validatePromoCode } from '@/util/user_client';
import type { RawEPCCPromotion } from '@/types/Cart';

import './PromoCodeInput.style.scss';

export type PromoCodeValidationResult = {
  code: string;
  isValid: boolean;
  isLoading: boolean;
  promotion?: RawEPCCPromotion;
  error?: string;
};

type PromoCodeInputProps = {
  className?: string;
  tierId: string;
  insuranceId: string;
  initialCode?: string;
  inputPlaceholder?: string;
  buttonText?: string;
  successMessage?: string;
  errorMessage?: string;
  backgroundColor?: string;
  borderColor?: string;
  accentColor?: string;
  textColor?: string;
  builderState?: any; // Builder.io automatically provides this with context
  builderContext?: any; // Builder.io injects this (should not be passed to DOM)
  builderBlock?: any; // Builder.io injects this (should not be passed to DOM)
};

function PromoCodeInput(props: PromoCodeInputProps) {
  const {
    className = '',
    tierId,
    insuranceId,
    initialCode,
    inputPlaceholder = 'Enter code',
    buttonText = 'Update',
    successMessage = 'Success! {code} applied!',
    errorMessage = 'Promo code is not valid. Try again.',
    backgroundColor = 'transparent',
    borderColor = '#D1D5DB',
    accentColor = '#FFC0CB',
    textColor = '#1F2937',
    builderState,
    // Explicitly destructure Builder.io props to prevent them from leaking to DOM
    builderContext: _builderContext,
    builderBlock: _builderBlock
  } = props;

  // Builder.io automatically provides context via builderState.context
  const context = builderState?.context;

  const [currentPromoCode, setCurrentPromoCode] = useState(initialCode || '');
  const [promoCodeIsValid, setPromoCodeIsValid] = useState(false);
  const [promoCodeSubmitted, setPromoCodeSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [validationResult, setValidationResult] =
    useState<PromoCodeValidationResult | null>(null);

  // Update parent state via context callback when validation result changes
  useEffect(() => {
    if (context?.updatePromoValidation) {
      context.updatePromoValidation(validationResult);
    }
  }, [validationResult, context]);

  // Automatically validate if initialCode is provided
  useEffect(() => {
    if (initialCode && tierId && insuranceId) {
      checkPromoCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkPromoCode = async () => {
    if (!currentPromoCode) {
      return;
    }

    // Fire analytics callback when user clicks submit
    if (context?.onPromoCodeSubmit) {
      context.onPromoCodeSubmit(currentPromoCode);
    }

    setIsLoading(true);
    setPromoCodeSubmitted(false);

    try {
      const validationResponse = await validatePromoCode(
        currentPromoCode,
        tierId,
        insuranceId
      );

      setPromoCodeSubmitted(true);
      setPromoCodeIsValid(validationResponse.valid);

      // Create validation result for Builder state
      const result: PromoCodeValidationResult = {
        code: currentPromoCode,
        isValid: validationResponse.valid,
        isLoading: false,
        promotion: validationResponse.promotion,
        error: validationResponse.error
      };

      setValidationResult(result);
    } catch (error) {
      setPromoCodeSubmitted(true);
      setPromoCodeIsValid(false);

      // Create error result for Builder state
      const result: PromoCodeValidationResult = {
        code: currentPromoCode,
        isValid: false,
        isLoading: false,
        error: 'Validation failed'
      };

      setValidationResult(result);
    } finally {
      setIsLoading(false);
    }
  };

  const formatMessage = (template: string) => {
    return template.replace('{code}', currentPromoCode);
  };

  return (
    <div
      className={cn('PromoCodeInput', className)}
      style={
        {
          '--promo-bg-color': backgroundColor,
          '--promo-border-color': borderColor,
          '--promo-accent-color': accentColor,
          '--promo-text-color': textColor
        } as React.CSSProperties
      }
    >
      <div className='PromoCodeInput-section'>
        <Text
          variant='title-xxs-700-upper'
          className='PromoCodeInput-heading'
        >
          Promo code
        </Text>
        <div className='PromoCodeInput-input-group'>
          <input
            type='text'
            value={currentPromoCode}
            onChange={(e) => {
              setPromoCodeSubmitted(false);
              setCurrentPromoCode(e.target.value);
            }}
            className='PromoCodeInput-input'
            placeholder={inputPlaceholder}
            disabled={isLoading}
          />
          <button
            type='button'
            onClick={checkPromoCode}
            disabled={isLoading || !currentPromoCode}
            className={cn('PromoCodeInput-button', {
              'is-loading': isLoading
            })}
          >
            {isLoading ? 'Validating...' : buttonText}
          </button>
        </div>
        {promoCodeSubmitted &&
          (promoCodeIsValid ? (
            <Text
              className='PromoCodeInput-success'
              variant='text-s'
            >
              {formatMessage(successMessage)}
            </Text>
          ) : (
            <Text
              className='PromoCodeInput-error'
              variant='text-s'
            >
              {formatMessage(errorMessage)}
            </Text>
          ))}
      </div>
    </div>
  );
}

export default PromoCodeInput;
